/**
 * Pricing model for the Teamward quote builder.
 *
 * Vendor listings in src/data.js use price tiers rather than dollar figures.
 * This file contains the client-safe numbers needed to build estimates.
 * Private source URLs, partner terms, and commission rates must not be placed
 * in the public React application.
 */

export {
  FOOD_CATEGORY_ORDER,
  FOOD_OPTIONS,
  LEGACY_FOOD_OPTIONS,
  foodOptionForId,
} from './foodOptions';

// -------------------------------------------------------------------------
// Taxes and service charges
// -------------------------------------------------------------------------

export const TAX_RATES = {
  Denver: {
    foodAndDrink: 0.08,
    label: 'Denver food & drink tax (8.00%)',
    capturedOn: '2026-08-22',
  },
};

export const SERVICE_CHARGE_ALLOWANCE_RATE = 0.2;

// -------------------------------------------------------------------------
// Per-vendor rates
// -------------------------------------------------------------------------

export const VENDOR_RATES = {
  // Hosted dining experiences: per-vendor prices researched 2026-10-28.
  'denver-topgolf-centennial': {
    basis: 'per_person',
    low: 47,
    high: 47,
    startingAt: true,
    confidence: 'published',
    capturedOn: '2026-10-28',
    note:
      'Topgolf Centennial event packages for 13+ players start at $47 per person ' +
      '(bay time plus casual fare or buffet), before tax, service charge, and admin fees. ' +
      'Groups over 24 book through Topgolf event sales. Source: topgolf.com/us/centennial/plan-an-event/company-events/',
    includesFood: true,
  },
  'denver-delicious-food-tours': {
    basis: 'per_person',
    low: 90,
    high: 135,
    confidence: 'published',
    capturedOn: '2026-10-28',
    note:
      'Private tours: Downtown Food Tour $90/guest ($120 with drinks, max 25); Dinner Tasting Tour ' +
      '$135/guest. Prices include the guide, all tastings, tax, fees, and gratuity; $200 deposit per ' +
      'time slot. Source: deliciousdenverfoodtours.com/corporate-events/',
    includesFood: true,
  },
  'denver-stir-cooking-school': {
    basis: 'per_person',
    low: 95,
    high: 150,
    confidence: 'benchmark',
    capturedOn: '2026-10-28',
    note:
      'Stir charges private groups of 12+ its regular per-person class price with no rental fee, tax ' +
      'included; drinks are extra. Range is from TableJourney (third-party) class listings, so Teamward ' +
      'confirms the class price. Source: stirtolearn.com/faq',
    includesFood: true,
  },
  'denver-cook-street-school': {
    basis: 'per_person',
    low: 55,
    high: 125,
    confidence: 'benchmark',
    capturedOn: '2026-10-28',
    note:
      'Based on Cook Street public class prices ($55–$125 per person). Private classes (10–35 guests) ' +
      'include chef instruction, a 3-course meal, and beer and wine, but the private rate is not ' +
      'published. Source: cookstreet.com/private-events/',
    includesFood: true,
  },
  'denver-food-lab': {
    basis: 'per_person',
    low: 119,
    high: 135,
    confidence: 'benchmark',
    capturedOn: '2026-10-28',
    note:
      'Based on Food Lab Denver public class prices ($119–$135). The corporate rate is not published. ' +
      'Source: foodlabinc.com/denver/cooking-classes',
    includesFood: true,
  },
  'denver-taste-buds-kitchen-centennial': {
    basis: 'per_person',
    low: 85,
    high: 100,
    confidence: 'benchmark',
    capturedOn: '2026-10-28',
    note:
      'Public classes start at $85 per adult; corporate events add an automatic 18% gratuity ' +
      '(about $100 per person) and have an unpublished event minimum. ' +
      'Source: tastebudskitchen.com/centennial/corporate-cooking-events/',
    includesFood: true,
  },
  'denver-whiskey-hitch-cocktail-class': {
    basis: 'per_person',
    low: 50,
    high: 75,
    minimum: 500,
    confidence: 'published',
    capturedOn: '2026-10-28',
    note:
      '$25 per cocktail per person, 2–3 cocktails, $500 class minimum. Includes instructor, bar kits, ' +
      'mixers, garnishes, glassware, and ice; your group supplies spirits and the space. ' +
      'Source: whiskeyhitch.com/cocktail-classes',
    includesFood: false,
  },
  'denver-cozymeal-team-cooking': {
    basis: 'per_person',
    low: 85,
    high: 115,
    confidence: 'published',
    capturedOn: '2026-10-28',
    note:
      'Cozymeal lists Denver team cooking classes at $85–$115 per person, including the chef, ' +
      'ingredients, equipment, and the meal. Exact price depends on the chef and menu. ' +
      'Source: cozymeal.com/denver/cooking-classes/group',
    includesFood: true,
  },
  'denver-this-is-a-clue-self-hosted': {
    basis: 'flat',
    low: 150,
    high: 150,
    confidence: 'confirmed',
    capturedOn: '2026-10-26',
    note: 'Ready-to-run deck, picture round, and scoring key; your colleague hosts. Flat base price regardless of group size.',
  },
  'denver-this-is-a-clue-hosted': {
    basis: 'flat',
    low: 250,
    high: 250,
    confidence: 'confirmed',
    capturedOn: '2026-10-26',
    note: 'Trained host, sound gear, scorecards, and live scoring at your venue. Flat base price regardless of group size. Additional hosts and travel, if needed, are quoted separately.',
  },
  'denver-this-is-a-clue-custom': {
    basis: 'flat',
    low: 300,
    high: 500,
    confidence: 'confirmed',
    capturedOn: '2026-10-26',
    note: 'Custom game starts at $300; extensive customization and multiple feedback rounds can cost up to $500. Confirm final scope, hosting arrangements, additional hosts, and travel before booking.',
  },
  'denver-brad-montgomery': {
    basis: 'flat',
    low: 15000,
    high: 15000,
    confidence: 'confirmed',
    capturedOn: '2026-10-07',
    note:
      'Keynote up to 90 minutes. Travel and lodging are additional. ' +
      'Other programs and any local pricing adjustments are quoted separately.',
  },
  'denver-bad-axe-throwing': {
    basis: 'tiered_units',
    unitLabel: 'lane',
    unitCapacity: 6,
    priceByUnitCount: {
      weekday: { 1: 450, 2: 750, 3: 1000 },
      weekend: { 1: 500, 2: 850, 3: 1150 },
    },
    confidence: 'published',
    capturedOn: '2026-08-21',
    note:
      'Published two-hour package totals cover one to three lanes (up to 18 ' +
      'people). Larger groups use the Denver category planning range until the ' +
      'vendor confirms a group-specific figure.',
  },

  'denver-puttshack': {
    basis: 'per_person',
    low: 36,
    high: 36,
    publishedMinHeadcount: 13,
    publishedMaxHeadcount: 18,
    confidence: 'published',
    capturedOn: '2026-08-21',
    note:
      'Event package published "starting at $36 per guest" for 13–18 guests. ' +
      'Outside that headcount range, Puttshack requires a custom event quote.',
  },

  'denver-punch-bowl-social': {
    basis: 'per_person',
    low: 40,
    high: 40,
    weekendLow: 50,
    weekendHigh: 50,
    confidence: 'published',
    capturedOn: '2026-08-21',
    note:
      'Bowling non-peak $20/person/hr, $40 for 2 hrs. Peak (Fri–Sat after 6pm) ' +
      '$25/person/hr, so $50 for 2 hrs. Priced here at the 2-hour duration that ' +
      'matches the listing.',
  },

  'denver-microbrew-tour': {
    basis: 'per_person',
    low: 65,
    high: 85,
    confidence: 'published',
    capturedOn: '2026-08-21',
    note:
      'Tour only $65/person plus gratuity; $75 with light appetizers; $85 with ' +
      'heavy appetizers. Range spans those tiers, so do not also add a food line ' +
      'for this vendor — the food is already in it.',
    includesFood: true,
  },

  'denver-public-library-team-room': {
    basis: 'flat',
    low: 0,
    high: 0,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Denver Public Library branch meeting spaces are available free of ' +
      'charge for qualifying reservations. Damage or cleaning charges are not ' +
      'included.',
  },

  'denver-rino-country-club': {
    basis: 'per_person',
    low: 3,
    high: 10,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Published mini-golf pricing is $3 for a day pass or $10 for a lifetime ' +
      'membership. Food, drinks, and private-event upgrades are separate.',
  },

  'denver-aqua-golf': {
    basis: 'per_person',
    low: 8,
    high: 24,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Published individual rates span an $8 small range bucket through a $24 ' +
      'extra-large bucket; adult mini golf is $12–$16. Group-event fees, if ' +
      'applicable, are separate.',
  },

  'denver-lucky-to-ride-bike-build': {
    basis: 'per_person',
    low: 75,
    high: 250,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'Teamward planning range for a facilitated charity bike build, including ' +
      'bike materials. Lucky To Ride does not publish a current package price, ' +
      'so confirm the final range directly.',
  },

  'denver-food-bank-rockies-group-volunteer': {
    basis: 'per_person',
    low: 0,
    high: 25,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'Teamward allowance for an otherwise free or donation-supported volunteer ' +
      'shift. Transportation, meals, or an optional company donation are not ' +
      'included.',
  },

  'denver-project-cure-group-volunteer': {
    basis: 'per_person',
    low: 0,
    high: 25,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'Teamward allowance for an otherwise free or donation-supported volunteer ' +
      'shift. Confirm any current group donation or materials expectation.',
  },

  'denver-park-trust-cleanup-day': {
    basis: 'per_person',
    low: 0,
    high: 25,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'Teamward allowance for supplies, refreshments, or a supporting donation. ' +
      'The organizer does not publish a standard corporate-group price.',
  },

  'denver-project-helping-virtual-kynd-kits': {
    basis: 'per_person',
    low: 15,
    high: 65,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Published Kynd Kit pricing spans the 100+ bulk Classic rate through the ' +
      'standard Max kit. Optional hosted facilitation and return distribution ' +
      'can add cost.',
  },

  'denver-water-world-group-gathering': {
    basis: 'per_person',
    low: 26,
    high: 42,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Published group tickets for 50–149 guests are $27.99–$41.99 and for ' +
      '150+ guests are $25.99–$39.99. Rounded planning range excludes food and ' +
      'pavilion rental.',
  },

  'denver-elitch-gardens-group-outing': {
    basis: 'per_person',
    low: 39,
    high: 55,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'The low end is anchored to Elitch Gardens’ published 50+ group starting ' +
      'price of $38.99. The upper end is a Teamward planning allowance because ' +
      'the final group rate and fees depend on the event.',
  },

  'denver-wings-over-rockies-group-visit': {
    basis: 'per_person',
    low: 15,
    high: 16,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Published adult group admission is $14.95 for an unguided visit and ' +
      '$15.95 for a private tour. Guided tours have separate capacity limits.',
  },

  'denver-lets-roam-large-group-scavenger-hunt': {
    basis: 'per_person',
    low: 35,
    high: 60,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'The low end is the published $35 per-person starting price. The upper ' +
      'end is a Teamward planning allowance for hosting and customization.',
  },

  'denver-teambonding-large-group-event': {
    basis: 'per_person',
    low: 45,
    high: 150,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'TeamBonding publishes typical catalog averages of $45–$60 per person ' +
      'without shipping and $85–$150+ with shipping. This uses the combined ' +
      'planning span rather than a program-specific quote.',
  },

  'denver-outback-large-group-team-building': {
    basis: 'per_person',
    low: 11,
    high: 400,
    confidence: 'benchmark',
    capturedOn: '2026-09-24',
    note:
      'Outback publishes a broad catalog span from about $11 per person for ' +
      'self-hosted formats to about $400 for premium facilitated programs. ' +
      'Select a format before relying on this intentionally wide range.',
  },

  'denver-project-helping-conference-kynd-build': {
    basis: 'per_person',
    low: 18,
    high: 44,
    confidence: 'published',
    capturedOn: '2026-09-24',
    note:
      'Derived from published 100+ bulk kit rates, local distribution, and a ' +
      'Denver host allowance. Custom branding, premium add-ons, and special ' +
      'shipping are separate.',
  },
};

// -------------------------------------------------------------------------
// Category benchmarks
// -------------------------------------------------------------------------

export const CATEGORY_BENCHMARKS = {
  'Escape Room': {
    low: 28,
    high: 40,
    basis: 'per_person',
    benchmarkCount: 3,
  },

  Games: {
    low: 32,
    high: 55,
    basis: 'per_person',
    benchmarkCount: 3,
  },

  'Team Building': {
    low: 25,
    high: 50,
    basis: 'per_person',
    benchmarkCount: 2,
  },

  'Scavenger Hunt': {
    low: 10,
    high: 30,
    basis: 'per_person',
    benchmarkCount: 3,
  },

  Active: {
    low: 45,
    high: 90,
    basis: 'per_person',
    benchmarkCount: 3,
  },

  Creative: {
    low: 40,
    high: 105,
    basis: 'per_person',
    benchmarkCount: 3,
  },

  Culture: {
    low: 18,
    high: 45,
    basis: 'per_person',
    benchmarkCount: 2,
    note:
      'Museum and cultural-institution group rates are usually admission plus a ' +
      'facility fee and are rarely published. Wide band on purpose.',
  },

  Outdoor: {
    low: 15,
    high: 60,
    basis: 'per_person',
    benchmarkCount: 2,
    note:
      'Outdoor spans cheap park rentals and botanic-garden style venue buyouts ' +
      'with minimum spends. A $$$ outdoor listing is usually a venue rental, ' +
      'not a per-person activity — verify before sending.',
  },

  'Food & Drinks': {
    low: 65,
    high: 130,
    basis: 'per_person',
    benchmarkCount: 3,
    includesFood: true,
  },

  Entertainment: {
    low: 20,
    high: 100,
    basis: 'per_person',
    benchmarkCount: 4,
    note:
      'Planning band spans group admission through premium reserved seating or ' +
      'a private-event allocation. Final inclusions vary substantially.',
  },

  'Active Games': {
    low: 3,
    high: 24,
    basis: 'per_person',
    benchmarkCount: 2,
  },

  'Purpose-Driven': {
    low: 0,
    high: 250,
    basis: 'per_person',
    benchmarkCount: 4,
    tiered: false,
    note:
      'Purpose-driven events range from free volunteer shifts to materials-heavy ' +
      'charity builds. Use the listing-specific estimate when available.',
  },

  Workspace: {
    low: 0,
    high: 750,
    basis: 'flat',
    benchmarkCount: 1,
    tiered: false,
  },

  'Leadership Training': {
    low: 2500,
    high: 15000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Team Communication': {
    low: 2500,
    high: 15000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Professional Development': {
    low: 1000,
    high: 6000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Leadership and Communication': {
    low: 3000,
    high: 20000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Leadership and Team Development': {
    low: 2500,
    high: 15000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Communication Training': {
    low: 1500,
    high: 10000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Workplace Wellness': {
    low: 750,
    high: 6000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Leadership Speaker': {
    low: 5000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Motivational Speaker': {
    low: 5000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Business Growth Speaker': {
    low: 5000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Workplace Culture Speaker': {
    low: 5000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'AI and Innovation Speaker': {
    low: 5000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Speaker Matching Service': {
    low: 5000,
    high: 30000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Botanical Retreat Venue': {
    low: 3000,
    high: 25000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Downtown Retreat Venue': {
    low: 1500,
    high: 8000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Executive Board Retreat': {
    low: 750,
    high: 5000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Creative Retreat Venue': {
    low: 3000,
    high: 20000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Hybrid Executive Retreat': {
    low: 1500,
    high: 10000,
    basis: 'flat',
    benchmarkCount: 3,
    tiered: false,
  },

  'Facilitated Virtual Retreat': {
    low: 45,
    high: 150,
    basis: 'per_person',
    benchmarkCount: 2,
    tiered: false,
  },

  'Environmental Volunteer Event': {
    low: 0,
    high: 25,
    basis: 'per_person',
    benchmarkCount: 3,
    tiered: false,
  },

  'Virtual Volunteer Event': {
    low: 15,
    high: 65,
    basis: 'per_person',
    benchmarkCount: 3,
    tiered: false,
  },

  'Park & Picnic': {
    low: 0,
    high: 500,
    basis: 'flat',
    benchmarkCount: 2,
    tiered: false,
    note:
      'Park access may be free; the upper end allows for a basic permit, games, ' +
      'or picnic setup. Food is separate.',
  },

  'Walking Trail': {
    low: 0,
    high: 0,
    basis: 'flat',
    benchmarkCount: 1,
    tiered: false,
    note:
      'No activity admission is assumed. Transportation, food, and optional ' +
      'facilitation are separate.',
  },
};

// -------------------------------------------------------------------------
// Facilitation add-on
// -------------------------------------------------------------------------

export const FACILITATION_OPTIONS = [
  {
    id: 'none',
    label: 'No facilitator',
    basis: 'flat',
    low: 0,
    high: 0,
  },
  {
    id: 'host',
    label: 'Professional host or emcee',
    basis: 'flat',
    low: 200,
    high: 995,
    confidence: 'benchmark',
    note:
      'Add a professional host to make introductions, keep the agenda moving, ' +
      'and guide transitions. Final pricing depends on format, duration, and ' +
      'event complexity.',
  },
  {
    id: 'speaker',
    label: 'Keynote speaker',
    basis: 'flat',
    low: 5000,
    high: 25000,
    confidence: 'benchmark',
    note:
      'Planning range for professional keynote speakers. Fees vary widely by ' +
      'speaker, travel, usage rights, and date; Teamward confirms the final fee.',
  },
];
// -------------------------------------------------------------------------
// Teamward planning support
// -------------------------------------------------------------------------

export const PLANNING_SUPPORT_OPTIONS = [
  {
    id: 'self',
    label: "I'll coordinate it myself",
    priceLabel: '$0',
    basis: 'flat',
    low: 0,
    high: 0,
    confidence: 'teamward',
    description:
      'You contact vendors and manage the event details yourself.',
    quoteDetail:
      'Self-coordinated; no planning-service fee',
  },
  {
    id: 'vendor-coordination',
    label: 'Teamward vendor coordination',
    priceLabel: '$300',
    basis: 'flat',
    low: 300,
    high: 300,
    confidence: 'teamward',
    badge: 'Most popular',
    description:
      'We confirm availability, collect final pricing, and coordinate ' +
      'communication with up to three selected vendors.',
    quoteDetail:
      'Includes coordination with up to three selected vendors',
  },
  {
    id: 'day-of',
    label: 'Day-of coordination',
    priceLabel: 'Starting at $1,000',
    basis: 'flat',
    low: 1000,
    high: 1000,
    confidence: 'teamward',
    startingAt: true,
    description:
      'We finalize the timeline, confirm vendor details, and manage up to ' +
      'eight hours of event-day logistics.',
    quoteDetail:
      'Base price for one coordinator and up to eight on-site hours',
  },
  {
    id: 'full-service',
    label: 'Full-service event planning',
    priceLabel: 'Starting at $2,500',
    basis: 'flat',
    low: 2500,
    high: 2500,
    confidence: 'teamward',
    startingAt: true,
    description:
      'We manage vendor selection, quotes, scheduling, budget details, and ' +
      'event-day execution from beginning to end.',
    quoteDetail:
      'Base price for standard full-service planning and coordination',
  },
];
// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

export function tierWindow(tier, low, high) {
  const span = high - low;

  switch (tier) {
    case '$':
      return {
        low,
        high: low + span * 0.35,
      };

    case '$$$':
      return {
        low: low + span * 0.55,
        high,
      };

    case '$$':
    default:
      return {
        low: low + span * 0.2,
        high: low + span * 0.75,
      };
  }
}

export function parseGroupSize(groupSize) {
  if (typeof groupSize !== 'string') return null;

  const matches = groupSize.replace(/,/g, '').match(/\d+/g);

  if (!matches || matches.length === 0) return null;

  const min = Number(matches[0]);
  const max = matches.length > 1 ? Number(matches[1]) : Infinity;

  return { min, max };
}

export function fitsHeadcount(vendor, headcount) {
  const range = parseGroupSize(vendor.groupSize);

  if (!range) return true;

  return headcount >= range.min && headcount <= range.max;
}

function benchmarkRateFor(vendor, note) {
  const benchmark =
    CATEGORY_BENCHMARKS[
      vendor.category
    ];

  if (!benchmark) {
    return {
      basis: 'quote_only',
      low: null,
      high: null,
      confidence: 'unknown',
      note:
        `No rate and no benchmark for category ` +
        `"${vendor.category}".`,
      includesFood: false,
    };
  }

  const window =
    benchmark.tiered === false
      ? {
          low: benchmark.low,
          high: benchmark.high,
        }
      : tierWindow(
          vendor.price,
          benchmark.low,
          benchmark.high
        );

  return {
    basis: benchmark.basis,
    low: Math.round(window.low),
    high: Math.round(window.high),
    confidence: 'benchmark',
    note: [
      note,
      benchmark.note,
    ]
      .filter(Boolean)
      .join(' '),
    includesFood: Boolean(
      benchmark.includesFood
    ),
  };
}

export function rateFor(
  vendor,
  { weekend = false, headcount = null } = {}
) {
  const explicit = VENDOR_RATES[vendor.id];

  if (explicit) {
    const belowPublishedRange =
      headcount != null &&
      explicit.publishedMinHeadcount != null &&
      headcount < explicit.publishedMinHeadcount;

    const abovePublishedRange =
      headcount != null &&
      explicit.publishedMaxHeadcount != null &&
      headcount > explicit.publishedMaxHeadcount;

    if (belowPublishedRange || abovePublishedRange) {
      return benchmarkRateFor(
        vendor,
        'The published package does not cover this headcount, so this uses the ' +
          'Denver category planning range instead.'
      );
    }

    if (explicit.basis === 'tiered_units') {
      const unitCapacity = explicit.unitCapacity || 1;

      const units =
        headcount == null
          ? null
          : Math.ceil(headcount / unitCapacity);

      const priceTable = weekend
        ? explicit.priceByUnitCount.weekend
        : explicit.priceByUnitCount.weekday;

      const packagePrice =
        units == null ? null : priceTable[units];

      if (packagePrice == null) {
        return benchmarkRateFor(
          vendor,
          'The published package does not cover this group size, so this uses ' +
            'the Denver category planning range instead.'
        );
      }

      return {
        basis: 'flat',
        low: packagePrice,
        high: packagePrice,
        confidence: explicit.confidence,
        capturedOn: explicit.capturedOn,
        pricingDetail:
          `${units} ${explicit.unitLabel}` +
          `${units === 1 ? '' : 's'} for up to ` +
          `${units * unitCapacity} people` +
          `${weekend ? ' (weekend rate)' : ''}`,
        note: explicit.note,
        includesFood: Boolean(explicit.includesFood),
      };
    }

    const useWeekend =
      weekend && explicit.weekendLow != null;

    return {
      basis: explicit.basis,
      low: useWeekend
        ? explicit.weekendLow
        : explicit.low,
      high: useWeekend
        ? explicit.weekendHigh
        : explicit.high,
      unitLabel: explicit.unitLabel,
      unitCapacity: explicit.unitCapacity,
      minimum: explicit.minimum,
      startingAt: Boolean(explicit.startingAt),
      confidence: explicit.confidence,
      capturedOn: explicit.capturedOn,
      note: explicit.note,
      includesFood: Boolean(explicit.includesFood),
    };
  }

  return benchmarkRateFor(vendor);
}

export const CONFIDENCE_LABELS = {
  confirmed: 'Confirmed with vendor',
  published: 'Vendor published rate',
  benchmark: 'Estimated from comparable Denver rates',
  allowance: 'Planning allowance',
  teamward: 'Teamward launch price',
  unknown: 'Pricing confirmation needed',
};

// Listings that stay in the site directory but are left out of the package
// builder because they publish no group price we can estimate from
// (researched 2026-10-28). Re-add once a price is published or confirmed.
export const QUOTE_BUILDER_EXCLUDED_VENDOR_IDS = new Set([
  'denver-the-lobby-corporate-events',
  'denver-milk-market',
  'denver-uncorked-kitchen',
  'denver-pedal-hopper',
  'denver-corporate-cooking-classes',
  'denver-denver-beer-co-private-event',
  'denver-woods-boss-brewing-event',
  'denver-mile-high-spirits',
  'denver-prost-biergarten-private-event',
]);
